import {
  useGetLayoutOptimizationsQuery,
  useGetPackagesByTypeQuery,
  useGetStructuralEngineeringsQuery,
  useGetBuildingPackagesQuery,
} from '@consigli/hooks';
import { DocumentPackageType, BuildingPackageType } from '@consigli/types';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type FetchHookParams =
  | { projectId: number; type: DocumentPackageType }
  | { projectId: number }
  | {
      projectId: number;
      page: number | 'all';
      type?: BuildingPackageType;
      pageSize?: number | null;
    };
export const useValidateName = (
  fetchHook:
    | typeof useGetPackagesByTypeQuery
    | typeof useGetStructuralEngineeringsQuery
    | typeof useGetLayoutOptimizationsQuery
    | typeof useGetBuildingPackagesQuery,
  params: FetchHookParams,
) => {
  const { t } = useTranslation();
  const isPackagesQuery = (hook: typeof fetchHook): hook is typeof useGetPackagesByTypeQuery =>
    hook === useGetPackagesByTypeQuery;

  const isStructuralQuery = (
    hook: typeof fetchHook,
  ): hook is typeof useGetStructuralEngineeringsQuery => hook === useGetStructuralEngineeringsQuery;

  const isLayoutQuery = (hook: typeof fetchHook): hook is typeof useGetLayoutOptimizationsQuery =>
    hook === useGetLayoutOptimizationsQuery;
  const isBuildingPackagesQuery = (
    hook: typeof fetchHook,
  ): hook is typeof useGetBuildingPackagesQuery => hook === useGetBuildingPackagesQuery;

  // Normalize to items that just need a name field
  const getItemName = (item: unknown): string => {
    if (typeof item === 'object' && item !== null && 'name' in item) {
      const value = (item as { name?: unknown }).name;
      return typeof value === 'string' ? value : '';
    }
    return '';
  };

  let data: Array<{ name: string }> = useMemo(() => [], []);
  if (isPackagesQuery(fetchHook)) {
    const { data: packagesData } = fetchHook(
      params as { projectId: number; type: DocumentPackageType },
    );
    data = (packagesData || []).map((p) => ({ name: getItemName(p) })).filter((p) => p.name);
  } else if (isStructuralQuery(fetchHook)) {
    const { data: structuralData } = fetchHook(params as { projectId: number });
    data = (structuralData || []).map((p) => ({ name: getItemName(p) })).filter((p) => p.name);
  } else if (isLayoutQuery(fetchHook)) {
    const { data: layoutData } = fetchHook(params as { projectId: number });
    data = (layoutData || []).map((p) => ({ name: getItemName(p) })).filter((p) => p.name);
  } else if (isBuildingPackagesQuery(fetchHook)) {
    const { data: buildingPackages } = fetchHook(
      params as { projectId: number; page: number | 'all'; type?: BuildingPackageType },
    );
    const results =
      buildingPackages && typeof buildingPackages === 'object' && 'results' in buildingPackages
        ? (buildingPackages as { results?: unknown[] }).results || []
        : [];
    data = results.map((p) => ({ name: getItemName(p) })).filter((p) => p.name);
  }
  const validatePackageName = useCallback(
    (value: string) => {
      if (data.find((pkg) => pkg.name === value)) return t('package-creation.package-name-taken');
      return Boolean(value && !data.find((pkg) => pkg.name === value));
    },
    [t, data],
  );
  return validatePackageName;
};
